/**
 * P1 Extension System: Host Extension Review Page
 * 
 * Allows hosts to view and manage extension requests for their vehicles.
 * Features:
 * - View all pending extension requests
 * - Review extension details (odometer, service warnings, bond status)
 * - Approve or reject extensions
 * - Record odometer checkpoint on approval
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  CalendarPlus, Clock, CheckCircle, XCircle, 
  AlertTriangle, Car, User, DollarSign, Gauge, Shield, Loader2,
  Calendar, Wrench, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { HostAppShell } from '../components/HostAppShell';

const HostExtensions = () => {
  const { api, user } = useAuth();
  
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  
  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  
  // Decision form state
  const [odometerCheckpoint, setOdometerCheckpoint] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  // Fetch extensions
  const fetchExtensions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/owner/extensions?status=${statusFilter}`);
      setExtensions(response.data?.extensions || []);
    } catch (error) {
      console.error('Failed to fetch extensions:', error);
      toast.error('Failed to load extension requests');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => {
    fetchExtensions();
  }, [fetchExtensions]);

  // Open review modal and fetch details
  const handleOpenReview = async (extension) => {
    setSelectedExtension(extension);
    setShowReviewModal(true);
    setReviewLoading(true);
    setShowRejectForm(false);
    setRejectReason('');
    setOdometerCheckpoint('');
    
    try {
      const response = await api.get(`/owner/extensions/${extension.extension_id}/review`);
      setReviewData(response.data);
      
      // Pre-fill odometer if available
      if (response.data.odometer?.current_reading) {
        setOdometerCheckpoint(response.data.odometer.current_reading.toString());
      }
    } catch (error) {
      toast.error('Failed to load extension details');
      setShowReviewModal(false);
    } finally {
      setReviewLoading(false);
    }
  };

  // Handle approve - P0 EXTENSION WORKFLOW: Now just approves, renter pays later
  const handleApprove = async () => {
    try {
      setDecisionLoading(true);
      const response = await api.post(`/owner/extensions/${selectedExtension.extension_id}/decision`, {
        decision: 'APPROVE',
        reason: 'Approved by host'
      });
      
      toast.success('Extension approved! Renter has been notified to complete payment.');
      setShowReviewModal(false);
      await fetchExtensions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve extension');
    } finally {
      setDecisionLoading(false);
    }
  };

  // Handle reject
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      setDecisionLoading(true);
      await api.post(`/owner/extensions/${selectedExtension.extension_id}/decision`, {
        decision: 'REJECT',
        reason: rejectReason
      });
      
      toast.success('Extension request declined');
      setShowReviewModal(false);
      await fetchExtensions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject extension');
    } finally {
      setDecisionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-700';
      case 'APPROVED_AWAITING_PAYMENT': return 'bg-blue-100 text-blue-700';
      case 'APPROVED_AWAITING_BOND': return 'bg-purple-100 text-purple-700';
      case 'APPROVED': return 'bg-green-100 text-green-700';
      case 'REJECTED': return 'bg-red-100 text-red-700';
      case 'PAYMENT_FAILED': return 'bg-red-100 text-red-700';
      case 'EXPIRED': return 'bg-slate-100 text-slate-700';
      case 'CANCELLED': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };
  
  const getStatusLabel = (status) => {
    switch (status) {
      case 'APPROVED_AWAITING_PAYMENT': return 'Awaiting Payment';
      case 'APPROVED_AWAITING_BOND': return 'Awaiting Bond';
      case 'PAYMENT_FAILED': return 'Payment Failed';
      default: return status;
    }
  };

  return (
    <HostAppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Extension Requests</h1>
          <p className="text-slate-600 mt-1">Review and manage booking extension requests</p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['PENDING', 'APPROVED', 'REJECTED', 'ALL'].map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status === 'ALL' ? '' : status)}
              className={statusFilter === status ? 'bg-[#004B8D]' : ''}
              data-testid={`filter-${status.toLowerCase()}`}
            >
              {status}
            </Button>
          ))}
        </div>

        {/* Extensions List */}
        <div>
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#004B8D]" />
            <p className="text-slate-500 mt-2">Loading extensions...</p>
          </div>
        ) : extensions.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <CalendarPlus className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No Extension Requests</h3>
              <p className="text-slate-500 mt-1">
                {statusFilter === 'PENDING' 
                  ? 'No pending extension requests for your vehicles.'
                  : 'No extension requests match your filter.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {extensions.map((ext) => (
              <Card 
                key={ext.extension_id} 
                className={`border-l-4 ${
                  ext.status === 'PENDING' ? 'border-l-yellow-500' :
                  ext.status === 'APPROVED' ? 'border-l-green-500' :
                  'border-l-slate-300'
                }`}
                data-testid={`extension-card-${ext.extension_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    {/* Extension Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getStatusColor(ext.status)}>
                          {ext.status?.replace('_', ' ')}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {format(parseISO(ext.created_at), 'dd MMM yyyy, HH:mm')}
                        </span>
                      </div>
                      
                      {/* Vehicle */}
                      <div className="flex items-center gap-2 text-sm mb-1">
                        <Car className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">
                          {ext.vehicle?.make} {ext.vehicle?.model}
                        </span>
                      </div>
                      
                      {/* Renter */}
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span>{ext.renter?.full_name || 'Unknown Renter'}</span>
                      </div>
                      
                      {/* Dates */}
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span>Current: {ext.original_end_at}</span>
                        </div>
                        <div className="flex items-center gap-1 text-blue-600">
                          <CalendarPlus className="w-4 h-4" />
                          <span>New: {ext.requested_end_at}</span>
                        </div>
                      </div>
                      
                      {/* Pricing */}
                      <div className="flex items-center gap-2 mt-2 text-sm">
                        <DollarSign className="w-4 h-4 text-green-500" />
                        <span className="font-semibold text-green-600">
                          +{ext.pricing_breakdown?.extra_days} days = ${ext.pricing_breakdown?.total?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    {ext.status === 'PENDING' && (
                      <Button
                        onClick={() => handleOpenReview(ext)}
                        className="bg-[#004B8D] hover:bg-[#003A6D]"
                        data-testid={`review-btn-${ext.extension_id}`}
                      >
                        Review Request
                      </Button>
                    )}
                    
                    {ext.status === 'APPROVED' && (
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Approved</span>
                        </div>
                        <span className="text-xs text-slate-500">
                          {ext.decided_at && format(parseISO(ext.decided_at), 'dd MMM')}
                        </span>
                      </div>
                    )}
                    
                    {ext.status === 'REJECTED' && (
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-red-600">
                          <XCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Declined</span>
                        </div>
                        {ext.decision_reason && (
                          <p className="text-xs text-slate-500 mt-1 max-w-[200px] truncate">
                            {ext.decision_reason}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={(open) => {
        if (!open && !decisionLoading) {
          setShowReviewModal(false);
          setSelectedExtension(null);
          setReviewData(null);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-blue-600" />
              Review Extension Request
            </DialogTitle>
          </DialogHeader>
          
          {reviewLoading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#004B8D]" />
              <p className="text-slate-500 mt-2">Loading details...</p>
            </div>
          ) : reviewData && (
            <div className="space-y-4 py-4">
              {/* Vehicle & Renter Info */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Car className="w-4 h-4 text-slate-500" />
                  <span className="font-medium">
                    {reviewData.vehicle?.year} {reviewData.vehicle?.make} {reviewData.vehicle?.model}
                  </span>
                  {reviewData.vehicle?.rego && (
                    <Badge variant="outline" className="text-xs">{reviewData.vehicle.rego}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <User className="w-4 h-4 text-slate-400" />
                  <span>{reviewData.renter?.full_name}</span>
                </div>
              </div>
              
              {/* Extension Details */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-xs text-blue-600 mb-1">Current Return</div>
                  <div className="font-medium">{reviewData.booking?.current_end_date}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xs text-green-600 mb-1">New Return</div>
                  <div className="font-medium">{reviewData.extension?.requested_end_at}</div>
                </div>
              </div>
              
              {/* Pricing */}
              <div className="bg-slate-50 rounded-lg p-3">
                <h4 className="font-medium text-sm mb-2">Pricing Breakdown</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Extra Days</span>
                    <span>{reviewData.extension?.pricing_breakdown?.extra_days}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Daily Rate</span>
                    <span>${reviewData.extension?.pricing_breakdown?.rate_per_day?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Subtotal</span>
                    <span>${reviewData.extension?.pricing_breakdown?.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Platform Fee ({reviewData.extension?.pricing_breakdown?.host_fee_percent || 5}%)</span>
                    <span>-${(reviewData.extension?.pricing_breakdown?.host_fee || reviewData.extension?.pricing_breakdown?.platform_fee)?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t border-slate-200">
                    <span>Guest Pays (incl. {reviewData.extension?.pricing_breakdown?.renter_fee_percent || 12}% service fee)</span>
                    <span className="text-slate-800">${reviewData.extension?.pricing_breakdown?.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-600 font-semibold">
                    <span>Your Payout</span>
                    <span>${reviewData.extension?.pricing_breakdown?.owner_payout?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Service Warnings */}
              {reviewData.service_warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-800 mb-2">
                    <Wrench className="w-4 h-4" />
                    <span className="font-medium text-sm">Service Warnings</span>
                  </div>
                  {reviewData.service_warnings.map((warning, idx) => (
                    <div key={idx} className="text-sm text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-3 h-3" />
                      {warning.message}
                    </div>
                  ))}
                </div>
              )}
              
              {/* P0 EXTENSION WORKFLOW: Renter-Provided Data */}
              {reviewData.renter_data && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-green-800">
                    <User className="w-4 h-4" />
                    Renter-Provided Information
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">Odometer Reading</span>
                      <span className="font-medium">{reviewData.renter_data.odometer_reading?.toLocaleString()} km</span>
                    </div>
                    
                    {reviewData.renter_data.odometer_photo_url && (
                      <div>
                        <p className="text-slate-600 mb-1">Odometer Photo:</p>
                        <img 
                          src={reviewData.renter_data.odometer_photo_url} 
                          alt="Odometer" 
                          className="w-full h-32 object-cover rounded-lg border border-green-200"
                        />
                      </div>
                    )}
                    
                    {reviewData.renter_data.licence_declaration?.declared && (
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-4 h-4" />
                        <span>Licence validity declared</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Legacy Request Note (no renter data) */}
              {!reviewData.renter_data?.odometer_reading && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2 text-amber-800">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium">Legacy Extension Request</p>
                      <p className="text-xs mt-1">This request was made before odometer/licence capture was required. The renter should be contacted to verify details.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Bond Status */}
              {reviewData.bond_status && reviewData.bond_status.bond_amount_cents > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-purple-800 mb-2">
                    <Shield className="w-4 h-4" />
                    <span className="font-medium text-sm">Bond Status</span>
                  </div>
                  <div className="text-sm text-purple-700">
                    <p>Current: {reviewData.bond_status.original_mode?.toUpperCase()}</p>
                    <p>Required Action: <strong>{reviewData.bond_status.action_required?.toUpperCase()}</strong></p>
                    <p className="text-xs mt-1">{reviewData.bond_status.action_reason}</p>
                  </div>
                </div>
              )}
              
              {/* P0 FIX: Post-hire notice (replaces toll context) */}
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-start gap-2 text-slate-600">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-xs">Tolls, fuel, cleaning, and damages are handled after the trip through the normal post-hire process. Extension only extends booking time.</p>
                </div>
              </div>
              
              {/* Reject Form */}
              {showRejectForm && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <h4 className="font-medium text-sm mb-2 text-red-800">Reason for Declining *</h4>
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Please explain why you're declining this extension..."
                    rows={3}
                    className="mb-2"
                    data-testid="reject-reason-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRejectForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              
              {/* Info Notice */}
              <div className="flex items-start gap-2 text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg p-3">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-500" />
                <p>
                  If you approve this extension, the renter will be notified to complete payment within 24 hours. 
                  The booking end date will only be updated after they pay.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {!showRejectForm ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowRejectForm(true)}
                  disabled={decisionLoading}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="decline-btn"
                >
                  Decline
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={decisionLoading}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="approve-btn"
                >
                  {decisionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Extension
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                onClick={handleReject}
                disabled={decisionLoading || !rejectReason.trim()}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-reject-btn"
              >
                {decisionLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Confirm Decline
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </HostAppShell>
  );
};

export default HostExtensions;
